import { useEffect } from 'react';
import { Box, Button, Text } from '@rsos/base-components';
import {
  clearAllFlagOverrides,
  clearFlagOverride,
  getFlagOverrides,
  getResolvedFlags,
  setFlagOverride,
  useFlagsVersion,
} from '@rsos/utils/openFeature';
import V2FlagOverrideItem from './V2FlagOverrideItem';
import { FLAG_METADATA } from './flagMetadata';

// Equality for flag values — JSON-compare objects (object/JSON flags), === otherwise.
const sameValue = (a: unknown, b: unknown): boolean =>
  typeof a === 'object' || typeof b === 'object'
    ? JSON.stringify(a) === JSON.stringify(b)
    : a === b;

// The list comes from the Datadog endpoint (what's actually live for this env), not
// a local list — so a flag not available for the env won't show here.
const V2FlagOverrides = () => {
  useFlagsVersion();

  const flags = getResolvedFlags();
  const overrides = getFlagOverrides();

  const endpointValues = new Map(flags.map(({ key, value }) => [key, value]));

  // Same rule as each row's •, plus keys the endpoint didn't return at all: those get
  // no row, so without them a devbox with no endpoint would report nothing overridden.
  // Sorted to match the list below.
  const overriddenKeys = Object.keys(overrides)
    .filter(
      key =>
        !endpointValues.has(key) ||
        !sameValue(overrides[key], endpointValues.get(key)),
    )
    .sort((a, b) => a.localeCompare(b));

  // Marked inline because an unlisted flag has no row to toggle or clear.
  const overriddenLabel = (key: string) =>
    endpointValues.has(key) ? key : `${key} (not in this env)`;

  // Auto-clear overrides that shouldn't linger:
  // - a no-op override equal to the endpoint value (would show as overridden without
  //   changing anything), and
  // - an orphaned non-boolean override with no real choice (no/one option) — e.g. a
  //   leftover after its registry options were removed.
  useEffect(() => {
    flags.forEach(({ key, type, value: endpointValue }) => {
      if (!(key in overrides)) return;
      if (sameValue(overrides[key], endpointValue)) {
        clearFlagOverride(key);
        return;
      }
      if (type === 'boolean') return;
      const options = FLAG_METADATA[key]?.options;
      if (!options || options.length <= 1) clearFlagOverride(key);
    });
  }, [flags, overrides]);

  return (
    <>
      {/* Outside the scroll region so the action can't scroll out of reach. */}
      <Box horizontal justify="space-between" align="flex-start" flow={8}>
        <Box grow>
          <Text size="small" color="secondaryText">
            {overriddenKeys.length === 0
              ? 'No overrides'
              : `${overriddenKeys.length} overridden: ${overriddenKeys
                  .map(overriddenLabel)
                  .join(', ')}`}
          </Text>
        </Box>
        <Button
          btnType="tertiary"
          size="xsmall"
          noMargin
          disabled={overriddenKeys.length === 0}
          dataName="reset-all-flag-overrides"
          onClick={clearAllFlagOverrides}
        >
          Reset all
        </Button>
      </Box>

      <Box
        height="360px"
        overflow="auto"
        border="1px solid"
        borderColor="heavyLineOutline"
      >
        {flags.length === 0 ? (
          <Box padding="16px">
            <Text size="normal" color="secondaryText">
              No flags available — not connected to the Datadog endpoint for
              this environment.
            </Text>
          </Box>
        ) : (
          flags.map(({ key, type, value: endpointValue }) => {
            const hasOverride = key in overrides;
            // The dot means "differs from Datadog", so an override equal to the
            // endpoint value doesn't count as overridden.
            const overridden =
              hasOverride && !sameValue(overrides[key], endpointValue);
            // Setting a value equal to the endpoint's is a no-op → clear instead.
            // For a boolean that makes flipping the switch back to the endpoint
            // value the way to reset it (there's no separate clear control).
            const applyOverride = (next: unknown) =>
              sameValue(next, endpointValue)
                ? clearFlagOverride(key)
                : setFlagOverride(key, type, next);
            return (
              <V2FlagOverrideItem
                key={key}
                flagKey={key}
                type={type}
                value={hasOverride ? overrides[key] : endpointValue}
                overridden={overridden}
                description={FLAG_METADATA[key]?.description}
                options={FLAG_METADATA[key]?.options}
                onToggle={(_flagKey, next) => applyOverride(next)}
                onSelect={(_flagKey, next) => applyOverride(next)}
                onClear={clearFlagOverride}
              />
            );
          })
        )}
      </Box>
    </>
  );
};

export default V2FlagOverrides;
